import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Plus,
  FileText,
  User,
  Shield,
  Briefcase,
  Paperclip,
  Check,
  X,
  Filter,
  Layers,
  Sparkles,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import { LeaveStatus, LeaveType, StaffLeaveRequest } from '../types';
import { useApp } from '../context/AppContext';

interface Props {
  isStaffSelfService?: boolean;
}

export const StaffLeaveManagement: React.FC<Props> = ({ isStaffSelfService = false }) => {
  const {
    leaveRequests,
    leaveQuotas,
    submitLeaveRequest,
    reviewLeaveRequest,
    currentUser,
  } = useApp();

  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | LeaveType>('all');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedRequestForReview, setSelectedRequestForReview] = useState<StaffLeaveRequest | null>(null);
  const [reviewerNote, setReviewerNote] = useState('');

  // Form State
  const [leaveType, setLeaveType] = useState<LeaveType>('sick');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalDays, setTotalDays] = useState('1');
  const [reason, setReason] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');

  // Calculate quota for current user or first quota
  const currentStaffQuota = leaveQuotas.find(
    (q) => q.staff_id === currentUser?.id || q.staff_name.includes(currentUser?.full_name?.split(' ')[0] || 'สมศักดิ์')
  ) || leaveQuotas[0];

  const filteredRequests = leaveRequests.filter((req) => {
    if (isStaffSelfService && currentUser?.role === 'staff') {
      if (req.staff_id !== currentUser.id && !req.staff_name.includes(currentUser.full_name?.split(' ')[0] || '')) {
        return false;
      }
    }
    const matchStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchType = typeFilter === 'all' || req.leave_type === typeFilter;
    return matchStatus && matchType;
  });

  const getLeaveTypeLabel = (type: LeaveType) => {
    switch (type) {
      case 'sick':
        return { label: 'ลาป่วย (Sick Leave)', color: 'bg-rose-50 text-rose-700 border-rose-200' };
      case 'personal':
        return { label: 'ลากิจ (Personal Leave)', color: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'vacation':
        return { label: 'ลาพักร้อน (Annual Vacation)', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'emergency':
        return { label: 'ลาฉุกเฉิน (Emergency)', color: 'bg-purple-50 text-purple-700 border-purple-200' };
      case 'maternity':
        return { label: 'ลาคลอด/ครอบครัว', color: 'bg-pink-50 text-pink-700 border-pink-200' };
    }
  };

  const getStatusBadge = (status: LeaveStatus) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
            <Clock className="w-3 h-3 text-amber-600" /> รออนุมัติ
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> อนุมัติแล้ว
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-200">
            <XCircle className="w-3 h-3 text-rose-600" /> ไม่อนุมัติ
          </span>
        );
    }
  };

  const handleSubmitLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    await submitLeaveRequest({
      staff_id: currentUser?.id || 'staff-01',
      staff_name: currentUser?.full_name || 'รปภ. สมศักดิ์ มั่งมี',
      department: 'ฝ่ายรักษาความปลอดภัยและบริการอาคาร',
      role_title: currentUser?.role === 'admin' ? 'เจ้าหน้าที่นิติบุคคล' : 'เจ้าหน้าที่รักษาความปลอดภัย',
      leave_type: leaveType,
      start_date: startDate,
      end_date: endDate,
      total_days: parseFloat(totalDays) || 1,
      reason: reason.trim(),
      attachment_url: attachmentUrl || undefined,
    });

    setShowSubmitModal(false);
    setReason('');
    setAttachmentUrl('');
  };

  const handleReviewAction = async (status: 'approved' | 'rejected') => {
    if (!selectedRequestForReview) return;
    await reviewLeaveRequest(
      selectedRequestForReview.id,
      status,
      reviewerNote.trim() || undefined,
      currentUser?.full_name || 'ผู้จัดการนิติบุคคล'
    );
    setSelectedRequestForReview(null);
    setReviewerNote('');
  };

  return (
    <div className="space-y-6">
      {/* Quota Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Sick Leave Quota */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">สิทธิลาป่วย (Sick Leave)</span>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-800">
                {currentStaffQuota.sick_total - currentStaffQuota.sick_used}
              </span>
              <span className="text-xs text-slate-500">/ {currentStaffQuota.sick_total} วันคงเหลือ</span>
            </div>
            <div className="mt-2 w-32 bg-slate-100 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-rose-500 h-full rounded-full"
                style={{ width: `${(currentStaffQuota.sick_used / currentStaffQuota.sick_total) * 100}%` }}
              />
            </div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 border border-rose-200 flex items-center justify-center">
            <Calendar className="w-5 h-5" />
          </div>
        </div>

        {/* Personal Leave Quota */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">สิทธิลากิจ (Personal Leave)</span>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-800">
                {currentStaffQuota.personal_total - currentStaffQuota.personal_used}
              </span>
              <span className="text-xs text-slate-500">/ {currentStaffQuota.personal_total} วันคงเหลือ</span>
            </div>
            <div className="mt-2 w-32 bg-slate-100 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-amber-500 h-full rounded-full"
                style={{ width: `${(currentStaffQuota.personal_used / currentStaffQuota.personal_total) * 100}%` }}
              />
            </div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center">
            <Briefcase className="w-5 h-5" />
          </div>
        </div>

        {/* Vacation Leave Quota */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">สิทธิลาพักร้อน (Annual Vacation)</span>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-800">
                {currentStaffQuota.vacation_total - currentStaffQuota.vacation_used}
              </span>
              <span className="text-xs text-slate-500">/ {currentStaffQuota.vacation_total} วันคงเหลือ</span>
            </div>
            <div className="mt-2 w-32 bg-slate-100 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-blue-600 h-full rounded-full"
                style={{ width: `${(currentStaffQuota.vacation_used / currentStaffQuota.vacation_total) * 100}%` }}
              />
            </div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 border border-blue-200 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Control Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-slate-500">สถานะ:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="bg-transparent font-medium text-slate-800 focus:outline-hidden cursor-pointer"
            >
              <option value="all">ทุกสถานะ ({leaveRequests.length})</option>
              <option value="pending">
                รออนุมัติ ({leaveRequests.filter((r) => r.status === 'pending').length})
              </option>
              <option value="approved">อนุมัติแล้ว</option>
              <option value="rejected">ไม่อนุมัติ</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs">
            <span className="text-slate-500">ประเภทการลา:</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="bg-transparent font-medium text-slate-800 focus:outline-hidden cursor-pointer"
            >
              <option value="all">ทั้งหมด</option>
              <option value="sick">ลาป่วย</option>
              <option value="personal">ลากิจ</option>
              <option value="vacation">ลาพักร้อน</option>
              <option value="emergency">ลาฉุกเฉิน</option>
            </select>
          </div>
        </div>

        {/* Submit Leave Button */}
        <button
          onClick={() => setShowSubmitModal(true)}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm shadow-emerald-600/20 transition-colors"
        >
          <Plus className="w-4 h-4" /> ส่งใบขอลาหยุดงาน
        </button>
      </div>

      {/* Leave Requests List */}
      <div className="space-y-3">
        {filteredRequests.length > 0 ? (
          filteredRequests.map((req) => {
            const badge = getLeaveTypeLabel(req.leave_type);
            const isPending = req.status === 'pending';

            return (
              <div
                key={req.id}
                className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-xs transition-all hover:border-slate-300 space-y-3"
              >
                {/* Header row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold text-sm">
                      {req.staff_name.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800 text-sm">{req.staff_name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.color}`}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {req.role_title} • {req.department}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-center">
                    {getStatusBadge(req.status)}
                    {!isStaffSelfService && isPending && (
                      <button
                        onClick={() => setSelectedRequestForReview(req)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors"
                      >
                        พิจารณาอนุมัติ
                      </button>
                    )}
                  </div>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className="p-2.5 rounded-xl bg-slate-50 text-slate-700 space-y-1">
                    <span className="text-[11px] text-slate-500 font-medium block">ช่วงเวลาที่ขอลา:</span>
                    <div className="font-semibold text-slate-800">
                      {req.start_date} ถึง {req.end_date} ({req.total_days} วัน)
                    </div>
                  </div>

                  <div className="sm:col-span-2 p-2.5 rounded-xl bg-slate-50 text-slate-700 space-y-1">
                    <span className="text-[11px] text-slate-500 font-medium block">เหตุผลประกอบการลา:</span>
                    <p className="text-slate-800">{req.reason}</p>
                  </div>
                </div>

                {/* Attachment or Review notes */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-xs">
                  {req.attachment_url ? (
                    <a
                      href={req.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline font-medium"
                    >
                      <Paperclip className="w-3.5 h-3.5" /> แนบเอกสารรับรองแพทย์ / ใบเสร็จ
                    </a>
                  ) : (
                    <span className="text-slate-400 text-[11px]">- ไม่มีเอกสารแนบ -</span>
                  )}

                  {req.reviewed_by && (
                    <div className="text-[11px] text-slate-500 italic">
                      พิจารณาโดย: <strong>{req.reviewed_by}</strong>
                      {req.reviewer_note && ` ("${req.reviewer_note}")`}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 space-y-2">
            <Calendar className="w-8 h-8 mx-auto opacity-40" />
            <p className="text-xs">ไม่พบรายการใบลาที่ตรงกับเงื่อนไข</p>
          </div>
        )}
      </div>

      {/* Submit Leave Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 bg-emerald-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <h3 className="font-bold text-base">ยื่นแบบฟอร์มขอลาหยุดงาน</h3>
              </div>
              <button
                onClick={() => setShowSubmitModal(false)}
                className="p-1 rounded-lg text-slate-200 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitLeave} className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">ประเภทการลา *</label>
                <select
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value as LeaveType)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                >
                  <option value="sick">ลาป่วย (Sick Leave)</option>
                  <option value="personal">ลากิจส่วนตัว (Personal Leave)</option>
                  <option value="vacation">ลาพักร้อนประจำปี (Annual Vacation)</option>
                  <option value="emergency">ลาฉุกเฉิน (Emergency)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ตั้งแต่วันที่ *</label>
                  <input
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ถึงวันที่ *</label>
                  <input
                    type="date"
                    required
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">จำนวนวันลา (วัน) *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  required
                  value={totalDays}
                  onChange={(e) => setTotalDays(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">เหตุผลประกอบการลา *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="ระบุรายละเอียด เช่น มีไข้สูงไปพบแพทย์, ธุระติดต่อทางราชการ..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">แนบลิงก์รูปใบรับรองแพทย์ / เอกสาร (ทางเลือก)</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={attachmentUrl}
                  onChange={(e) => setAttachmentUrl(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md shadow-emerald-600/20"
                >
                  ส่งใบลา
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal for Admin */}
      {selectedRequestForReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base">พิจารณาอนุมัติคำขอลา</h3>
              <button
                onClick={() => setSelectedRequestForReview(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                <div className="font-bold text-slate-800 text-sm">{selectedRequestForReview.staff_name}</div>
                <div className="text-slate-600">
                  ขอลา: <strong>{getLeaveTypeLabel(selectedRequestForReview.leave_type).label}</strong>
                </div>
                <div className="text-slate-600">
                  ระยะเวลา: {selectedRequestForReview.start_date} ถึง {selectedRequestForReview.end_date} (
                  {selectedRequestForReview.total_days} วัน)
                </div>
                <div className="text-slate-700 italic">"{selectedRequestForReview.reason}"</div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ความเห็นหรือหมายเหตุผู้อนุมัติ:</label>
                <input
                  type="text"
                  placeholder="เช่น อนุมัติและจัดเจ้าหน้าที่ประจำผลัดแทนเรียบร้อย"
                  value={reviewerNote}
                  onChange={(e) => setReviewerNote(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800"
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => handleReviewAction('rejected')}
                  className="px-4 py-2 rounded-xl bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-bold flex items-center gap-1"
                >
                  <XCircle className="w-4 h-4" /> ไม่อนุมัติ
                </button>

                <button
                  type="button"
                  onClick={() => handleReviewAction('approved')}
                  className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1 shadow-md shadow-emerald-600/20"
                >
                  <CheckCircle2 className="w-4 h-4" /> อนุมัติการลา
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
